import {Component, EventEmitter, HostListener, OnInit, Output,} from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Logger} from '../../logging/default-log.service';
import {DeliveryAddress} from '../../classes/deliveryaddress';
import {DeliveryDate} from '../../classes/deliverydate';
import {ActiveOrder} from '../../classes/active.order';
import {DeliveryService} from '../../service/delivery.service';
import {ActiveOrderService} from '../../service/active.order.service';
import {ApplicationService} from '../../service/application.service';
import {NumberFormatPipe} from '../../pipes/number.format.pipe';
import {CustomDatePipe} from '../../pipes/custom.date.pipe';
import {RenderPartOfDayComponent} from './render.part.of.day';
import {RenderDayOfWeekAbbreviationComponent} from './render.day.of.week.abbr';
import {RenderDayOfWeekComponent} from './render.day.of.week';
import {NgClass} from '@angular/common';
import {ProductOrderData} from '../../classes/product.order.data';

/**
 * Show popup if new order needs to be started. Contains functionality to close when clicking _outside_ the popup.
 *
 * @author evandongen
 */
@Component({
	selector: 'start-order',
	styleUrls: ['./start.order.component.scss'],
	templateUrl: 'start.order.component.html',
	standalone: true,
	imports: [
		NgClass,
		FormsModule,
		ReactiveFormsModule,
		RenderDayOfWeekComponent,
		RenderDayOfWeekAbbreviationComponent,
		RenderPartOfDayComponent,
		CustomDatePipe,
		NumberFormatPipe,
	],
})
export class StartOrderComponent implements OnInit {
	loading = true;
	loaded = false;
	popupOpen = false;
	@Output() closed = new EventEmitter<boolean>();
	@Output() saved = new EventEmitter<ActiveOrder>();
	shipmentDates: DeliveryDate[];
	deliveryAddresses: DeliveryAddress[];
	startNew: boolean = true;
	selectedOrderSelect: ActiveOrder;
	// Form bindings existing order
	form: UntypedFormGroup;
	// Form bindings new order
	shipmentDate: DeliveryDate;
	deliveryAddress: DeliveryAddress;

	constructor(
		private deliveryService: DeliveryService,
		private formBuilder: UntypedFormBuilder,
		public applicationService: ApplicationService,
		private activeOrderService: ActiveOrderService,
		private logger: Logger
	) {
		// subscribers
		this.fetchDeliveryDates()
			.then(() => {
				this.loading = false;
				this.popupOpen = true;
				this.preSelectFirstActiveOrder();
			})
			.catch(() => {
				// If something fails: close the modal, so the component will be destroyed.
				// If not destroyed we end up with a component which cannot be 'activated' again.
				this.close();
			});
	}

	get activeOrders(): Array<ActiveOrder> {
		return this.activeOrderService.activeOrders;
	}

	getActiveOrderForDeliveryDate(deliveryDate: DeliveryDate) {
		return this.activeOrders.find(
			activeOrder => activeOrder.shipmentDate === deliveryDate.shipmentDate
		);
	}

	getDeliveryDateForOrders(): Array<DeliveryDate> {
		const productData = this.getOptionalProductDataForStartOrderScope();

		return this.activeOrders
			.map(order => this.getDeliveryDateForOrder(order))
			.filter(deliveryDate => deliveryDate !== null && deliveryDate !== undefined)
			.filter(deliveryDate => !productData?.availableLaterDate ||
				this.isSameDate(deliveryDate.shipmentDate, productData.availableLaterDate));
	}

	ngOnInit() {
		this.form = this.formBuilder.group({
			selectedOrder: ['', Validators.required],
			shipmentDate: ['', Validators.required],
			deliveryAddress: ['', Validators.required]
		});

		this.loadDeliveryAddressesAndPreselectFirstAddress();
	}

	loadDeliveryAddressesAndPreselectFirstAddress() {
		const getDeliveryAddressesPromise = this.deliveryService
			.getDeliveryAddresses()
			.toPromise();

		getDeliveryAddressesPromise.then((deliveryAddresses: DeliveryAddress[]) => {
			this.deliveryAddresses = deliveryAddresses;
			this.deliveryAddress = this.deliveryAddresses[0];

			this.form.patchValue({deliveryAddress: this.deliveryAddress});

			this.loaded = true;
		});

		getDeliveryAddressesPromise.catch(error => {
			this.logger.error('Error while getting delivery addresses', error);
		});
	}

	getDeliveryDateForOrder(order: ActiveOrder): DeliveryDate {
		return this.shipmentDates.find(date => order && date && date.shipmentDate === order.shipmentDate);
	}

	/**
	 * Process form submit and notify services
	 * @param value
	 */
	onSubmit(value: any) {
		if (this.loading) {
			return;
		}

		const currentActiveOrder = this.activeOrderService.selectedOrder;

		if (this.startNew) {
			// shipment date is preselected and never null
			if (this.deliveryAddress) {
				this.loading = true;

				const productData = this.getOptionalProductDataForStartOrderScope();

				const deliveryDateObservable =
					this.deliveryService.getDeliveryDate(productData, this.shipmentDate.shipmentDate);

				// subscribers
				deliveryDateObservable
					.subscribe(
						(shipmentDate: DeliveryDate) => {
							if (shipmentDate == null) {
								this.logger.warn(
									'Error while getting delivery date for ' + this.shipmentDate.shipmentDate +
									'. Closing time may have passed. If this occurs frequently something else may be wrong. ' +
									'This error means an order can\'t be started for the selected delivery date.',
								);
								this.fetchDeliveryDates();
								return;
							}

							this.shipmentDate = shipmentDate;

							let order = this.findExistingOrder(this.shipmentDate, this.deliveryAddress);

							if (!order) {
								order = this.createActiveOrder(this.shipmentDate, this.deliveryAddress);
								this.activeOrderService.activeOrders?.push(order);
							}

							// If there is no active order or product data (new empty order), set the order as active.
							if (!currentActiveOrder || !productData) {
								this.activeOrderService.setSelectedOrder(order);
							}

							this.saved.emit(order);
						},
						error => {
							this.loading = false;
							this.logger.error(
								'Error while getting delivery date for ' +
								this.shipmentDate.shipmentDate,
								error
							);
						},
					);
			}

			return;
		}

		if (this.selectedOrderSelect) {
			this.loading = true;

			// Add to existing order
			if (!currentActiveOrder) {
				this.activeOrderService.setSelectedOrder(this.selectedOrderSelect);
			}

			this.saved.emit(this.selectedOrderSelect);
		}
	}

	changeDate() {
		this.shipmentDate = this.form.get('shipmentDate')?.value;
	}

	@HostListener('document:click', ['$event'])
	handleClick(event) {
		const clickedComponent = event.target;

		if (clickedComponent.className === 'modal-background') {
			this.close();
		}
	}

	/**
	 * Close the popup and notify listeners
	 */
	close(): void {
		// Make sure modal--hide is set, and close the popup application wide after 0.4s
		this.popupOpen = false;
		setTimeout(() => {
			this.closed.emit(false);
			this.applicationService.closePopup();
			this.activeOrderService.setSelectedOrder(null);
		}, 400);
	}

	private findExistingOrder(shipmentDate: DeliveryDate, deliveryAddress: DeliveryAddress): ActiveOrder {
		return this.activeOrderService.getActiveOrderForShipmentDateAndShipToCode(
			shipmentDate.shipmentDate,
			deliveryAddress.code
		);
	}

	private createActiveOrder(shipmentDate: DeliveryDate, deliveryAddress: DeliveryAddress): ActiveOrder {
		const activeOrder = new ActiveOrder();
		activeOrder.deliveryDate = shipmentDate.deliveryDate;
		activeOrder.shipmentDate = shipmentDate.shipmentDate;
		activeOrder.deliveryAddressName = deliveryAddress.name;
		activeOrder.shipToCode = deliveryAddress.code;
		activeOrder.volume = 0;

		return activeOrder;
	}

	/**
	 * Compares two dates and checks of one date is after the other.
	 * @param date a date.
	 * @param dateAfter the date that should be after the given date.
	 * @returns {boolean} true if the afterDate is after the given date.
	 */
	private isSameDate(date: string | number, dateAfter: string | number): boolean {
		if (!date || !dateAfter) {
			return false;
		}

		const beforeDate = new Date(date);
		const afterDate = new Date(dateAfter);

		this.resetTime(beforeDate);
		this.resetTime(afterDate);

		return afterDate.getTime() === beforeDate.getTime();
	}

	private resetTime(date: Date) {
		date.setHours(0, 0, 0, 0);
	}

	/**
	 * Makes sure the first delivery date / active order is selected
	 * when the user chooses for 'add to existing order'.
	 */
	private preSelectFirstActiveOrder() {
		const deliveryDateForOrders = this.getDeliveryDateForOrders();

		if (deliveryDateForOrders && deliveryDateForOrders.length > 0) {
			this.selectedOrderSelect = this.getActiveOrderForDeliveryDate(deliveryDateForOrders[0]);
			this.form.get('selectedOrder')?.setValue(this.selectedOrderSelect);
		}
	}

	/**
	 * Retrieve the delivery dates from the server and update the available delivery-/shipmentDates and the current
	 * shipmentDate. The list of activeOrderDeliveryDates are used for selecting an already started active order.
	 *
	 * Note: the name shipmentDate and deliveryDate is used. Both are of type DeliveryDate.
	 * This type contains shipmentDate, deliveryDate, closingTime, etc.
	 */
	private fetchDeliveryDates() {
		const productData = this.getOptionalProductDataForStartOrderScope();

		const promise = this.deliveryService.getDeliveryDates(productData).toPromise();

		promise.then((shipmentDates: DeliveryDate[]) => {
			this.shipmentDates = shipmentDates;
			// Select the first entry
			if (shipmentDates && shipmentDates.length > 0) {
				this.shipmentDate = shipmentDates[0];
				this.form.get('shipmentDate')?.setValue(this.shipmentDate);
			}
		});

		promise.catch(error => {
			this.logger.error('Error while getting delivery dates', error);
		});

		return promise;
	}

	private getOptionalProductDataForStartOrderScope(): ProductOrderData | null {
		return this.applicationService.startOrderPopupProductOrder;
	}
}
